import React, { useState, useEffect, useRef } from "react";
import { googleLogout } from "@react-oauth/google";

import Header from "./components/Header";
import GoogleAuth from "./components/GoogleAuth";
import EventConfirmation from "./components/EventConfirmation";
import EventInput, { type EventInputMode } from "./components/EventInput";
import LoginButton from "./components/LoginButton";
import TabNavigation, { type TabId } from "./components/TabNavigation";
import ContactsView from "./components/ContactsView";
import ImportContactsView from "./components/ImportContactsView";
import BulkEmailModal from "./components/BulkEmailModal";
import EmailHistoryView from "./components/EmailHistoryView";
import type { Contact } from "./components/types";
import type { CalendarAttachment, CalendarOperation } from "./types";
import {
  parseEventsFromTextWithProgress,
  warmupSchemaCache,
  type ProgressUpdate,
} from "./services/claudeService";
import {
  saveAuthToken,
  saveUserProfile,
  getCachedAuthToken,
  getCachedUserProfile,
  clearAuthCache,
  isTokenExpired,
  getTokenTimeRemaining,
  generateCodeVerifier,
  generateCodeChallenge,
  savePKCEVerifier,
  getPKCEVerifier,
  needsWarmup,
  recordRequest,
  type StoredToken,
  type UserProfile,
} from "./utils/authCache";

const App: React.FC = () => {
  // Auth state
  const [token, setToken] = useState<StoredToken | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const isAuthenticatingRef = useRef(false);
  const hasProcessedCallbackRef = useRef(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabId>("calendar");

  // App flow state
  const [userInput, setUserInput] = useState<string>("");
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileMediaType, setFileMediaType] = useState<string | null>(null);
  const [eventInputMode, setEventInputMode] = useState<EventInputMode>("text");
  const [parsedOperations, setParsedOperations] = useState<CalendarOperation[]>([]);
  const [isParsing, setIsParsing] = useState<boolean>(false);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [addSuccess, setAddSuccess] = useState<string | null>(null);
  const [operationAttachments, setOperationAttachments] = useState<Map<number, File[]>>(new Map());
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  const [displayProgress, setDisplayProgress] = useState<number>(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Contacts state (shared between ContactsView and ImportContactsView)
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [importToast, setImportToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  // Bulk email state
  const [emailModalContacts, setEmailModalContacts] = useState<Contact[] | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0);
  const [draftToEdit, setDraftToEdit] = useState<{
    draftId: string;
    subject: string;
    htmlBody: string;
    recipients: Contact[];
  } | null>(null);

  // Effect to smoothly animate progress bar
  useEffect(() => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    if (!progress) {
      setDisplayProgress(0);
      return;
    }

    const targetPercent = progress.percent;
    const currentPercent = displayProgress;

    // Cap at 1% before the milestone (so 30% milestone caps at 29%, 100% caps at 99%)
    const maxAutoIncrement = Math.max(0, targetPercent - 1);

    // If we're already at or past the auto-increment cap, just set it
    if (currentPercent >= maxAutoIncrement) {
      setDisplayProgress(maxAutoIncrement);
      return;
    }

    // Smooth animation - increment every 100ms
    progressIntervalRef.current = setInterval(() => {
      setDisplayProgress((prev: number) => {
        // Increment by 1% but don't exceed the cap (1% before milestone)
        const next = prev + 1;
        if (next >= maxAutoIncrement) {
          // Clear interval when we reach the cap
          if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
          }
          return maxAutoIncrement;
        }
        return next;
      });
    }, 100); // Update every 100ms for smooth animation

    // Cleanup interval on unmount or when progress changes
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [progress, displayProgress]);

  // Effect to handle OAuth redirect callback and load auth state
  useEffect(() => {
    const isDev =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    // Check if we have an authorization code in the URL (OAuth redirect callback)
    const urlParams = new URLSearchParams(window.location.search);
    const authCode = urlParams.get("code");

    if (authCode) {
      // Prevent double-processing if effect runs twice
      if (hasProcessedCallbackRef.current) {
        return;
      }
      hasProcessedCallbackRef.current = true;

      // Mark that we're in the middle of authentication
      isAuthenticatingRef.current = true;

      if (isDev) {
        console.log("🔐 [AUTH-CALLBACK] Received authorization code, exchanging for tokens");
        console.log("   - Code (first 10 chars):", authCode.substring(0, 10) + "...");
      }

      // Retrieve the PKCE code verifier we saved before redirect
      const codeVerifier = getPKCEVerifier();

      if (!codeVerifier) {
        console.error("❌ [AUTH-CALLBACK] PKCE code verifier not found");
        setError("Authentication failed: Security verification failed. Please try again.");
        isAuthenticatingRef.current = false;
        hasProcessedCallbackRef.current = false;
        window.history.replaceState(null, "", window.location.pathname);
        return;
      }

      if (isDev) {
        console.log("   - Code verifier (first 10 chars):", codeVerifier.substring(0, 10) + "...");
        console.log("   - Exchanging code for tokens via server...");
      }

      // Exchange authorization code for tokens via our server
      fetch("/api/auth/token", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: authCode,
          codeVerifier: codeVerifier,
          redirectUri: window.location.origin,
        }),
      })
        .then((res) => {
          if (!res.ok) {
            return res.json().then((err) => {
              throw new Error(err.error || "Failed to exchange authorization code for tokens");
            });
          }
          return res.json();
        })
        .then((tokenData) => {
          if (isDev) {
            console.log("✅ [AUTH-CALLBACK] Token exchange successful");
            console.log(
              "   - Access token (first 10 chars):",
              tokenData.access_token?.substring(0, 10) + "..."
            );
            console.log("   - Refresh token present:", !!tokenData.refresh_token);
            console.log("   - Expires in:", tokenData.expires_in, "seconds");
            console.log("   - Granted scopes:", tokenData.scope);
          }

          // If no refresh token received, redirect to consent screen immediately
          // This happens when user previously authorized with implicit flow
          if (!tokenData.refresh_token) {
            console.warn(
              "⚠ [AUTH-CALLBACK] No refresh token received. Redirecting to consent screen to obtain refresh token..."
            );
            // Set flag and trigger immediate re-login with consent
            sessionStorage.setItem("forceConsent", "true");
            clearAuthCache();
            setToken(null);
            isAuthenticatingRef.current = false;
            hasProcessedCallbackRef.current = false;
            window.history.replaceState(null, "", window.location.pathname);

            // Automatically redirect to consent screen
            setTimeout(() => {
              login();
            }, 1000); // Small delay to ensure state is cleared
            return;
          }

          // Verify all required scopes were granted
          const requiredScopes = [
            "https://www.googleapis.com/auth/calendar",
            "https://www.googleapis.com/auth/userinfo.profile",
            "https://www.googleapis.com/auth/userinfo.email",
            "https://www.googleapis.com/auth/gmail.send",
            "https://www.googleapis.com/auth/gmail.readonly",
            "https://www.googleapis.com/auth/contacts.readonly",
            "https://www.googleapis.com/auth/drive.file",
            "openid",
          ];

          const grantedScopes = tokenData.scope ? tokenData.scope.split(" ") : [];
          const missingScopes = requiredScopes.filter(
            (required) => !grantedScopes.includes(required)
          );

          if (missingScopes.length > 0) {
            console.error("❌ [AUTH-CALLBACK] Missing required permissions:", missingScopes);
            clearAuthCache();
            setToken(null);

            // Mark that we need to force consent on next login
            sessionStorage.setItem("forceConsent", "true");
            sessionStorage.setItem(
              "authRedirectReason",
              "Some required permissions weren't granted. Please sign in again and approve all requested permissions."
            );

            setError(
              `Authentication failed: Required permissions were not granted.\n\nMissing: ${missingScopes.join(", ")}\n\nPlease sign in again and grant all requested permissions.`
            );
            isAuthenticatingRef.current = false;
            hasProcessedCallbackRef.current = false;
            window.history.replaceState(null, "", window.location.pathname);
            return;
          }

          if (isDev) {
            console.log("✅ [AUTH-CALLBACK] All required scopes granted");
          }

          // Clear force consent flag on successful login
          sessionStorage.removeItem("forceConsent");

          // Create token response object
          const tokenResponse: StoredToken = {
            access_token: tokenData.access_token,
            refresh_token: tokenData.refresh_token,
            expires_in: tokenData.expires_in || 3600,
            token_type: tokenData.token_type || "Bearer",
            scope: tokenData.scope || "https://www.googleapis.com/auth/calendar",
          };

          // Save token to cache
          saveAuthToken(tokenResponse);
          setToken(tokenResponse);

          if (isDev) {
            console.log("   - Fetching user profile...");
          }

          // Fetch user profile
          return fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
            headers: { Authorization: `Bearer ${tokenData.access_token}` },
          }).then((res) => {
            if (!res.ok) {
              throw new Error("Failed to fetch user profile");
            }
            return res.json();
          });
        })
        .then((profile) => {
          // If profile is undefined, it means we returned early (no refresh token)
          // The error has already been shown to the user, so just skip processing
          if (!profile) {
            return;
          }

          if (isDev) {
            console.log("✅ [AUTH-CALLBACK] User profile fetched");
            console.log("   - Email:", profile.email);
            console.log("   - Name:", profile.name);
          }

          const userProfileData: UserProfile = {
            name: profile.name,
            picture: profile.picture,
            email: profile.email,
          };

          // Save profile to cache
          saveUserProfile(userProfileData);
          setUserProfile(userProfileData);
          // Authentication complete
          isAuthenticatingRef.current = false;

          if (isDev) {
            console.log("✅ [AUTH-CALLBACK] Authentication complete");
          }

          // Clean up: remove query params from URL AFTER everything is complete
          window.history.replaceState(null, "", window.location.pathname);
        })
        .catch((error) => {
          console.error("❌ [AUTH-CALLBACK] Error:", error);
          // Clear auth cache if authentication fails
          clearAuthCache();
          setToken(null);
          setError(
            `Authentication failed: ${error instanceof Error ? error.message : "Unknown error"}. Please try signing in again.`
          );
          // Authentication failed
          isAuthenticatingRef.current = false;
          hasProcessedCallbackRef.current = false; // Allow retry

          // Clean up: remove query params from URL even on error
          window.history.replaceState(null, "", window.location.pathname);
        });
    } else {
      // No authorization code - load existing auth state from cache
      const cachedToken = getCachedAuthToken();
      const cachedProfile = getCachedUserProfile();

      if (cachedToken && cachedProfile) {
        if (isDev) {
          console.log("🔐 [AUTH-INIT] Loading cached auth state");
          console.log("   - Email:", cachedProfile.email);
          console.log(
            "   - Token expires in:",
            Math.floor(getTokenTimeRemaining() / 1000 / 60),
            "minutes"
          );
        }
        setToken(cachedToken);
        setUserProfile(cachedProfile);
      } else if (cachedToken || cachedProfile) {
        // Partial cache - clear everything for consistency
        if (isDev) {
          console.log("⚠ [AUTH-INIT] Partial cache detected, clearing");
        }
        clearAuthCache();
        setToken(null);
        setUserProfile(null);
      }
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Effect to ensure token and profile stay synchronized
  useEffect(() => {
    // Skip sync check if we're in the middle of authenticating
    if (isAuthenticatingRef.current) {
      return;
    }

    // If no token but there's a profile, clear the profile for consistency
    if (!token && userProfile) {
      setUserProfile(null);
      clearAuthCache();
    }
    // If token exists but no profile, clear token for consistency
    if (token && !userProfile) {
      setToken(null);
      clearAuthCache();
    }
  }, [token, userProfile]);

  // Effect to automatically refresh token before expiration
  useEffect(() => {
    const isDev =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    if (!token) {
      return;
    }

    // If no refresh token, fall back to old behavior (force re-login on expiration)
    if (!token.refresh_token) {
      if (isDev) {
        console.log("⚠ [AUTH-REFRESH] No refresh token available, using legacy expiration flow");
      }

      // Check token expiration every minute
      const intervalId = setInterval(() => {
        const timeRemaining = getTokenTimeRemaining();
        const minutesRemaining = timeRemaining / (60 * 1000);

        if (minutesRemaining <= 0) {
          handleAuthExpiration();
        }
      }, 60000);

      return () => clearInterval(intervalId);
    }

    // We have a refresh token - enable automatic refresh

    const refreshToken = async () => {
      if (isDev) {
        console.log("🔄 [AUTH-REFRESH] Starting automatic token refresh");
        console.log(
          "   - Refresh token (first 10 chars):",
          token.refresh_token?.substring(0, 10) + "..."
        );
      }

      try {
        const response = await fetch("/api/auth/refresh", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            refreshToken: token.refresh_token,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to refresh token");
        }

        const tokenData = await response.json();

        if (isDev) {
          console.log("✅ [AUTH-REFRESH] Token refreshed successfully");
          console.log(
            "   - New access token (first 10 chars):",
            tokenData.access_token?.substring(0, 10) + "..."
          );
          console.log("   - Expires in:", tokenData.expires_in, "seconds");
        }

        // Update token with new access token (keep existing refresh token)
        const updatedToken: StoredToken = {
          access_token: tokenData.access_token,
          refresh_token: token.refresh_token, // Keep existing refresh token
          expires_in: tokenData.expires_in || 3600,
          token_type: tokenData.token_type || "Bearer",
          scope: tokenData.scope || token.scope,
        };

        // Save updated token to cache
        saveAuthToken(updatedToken);
        setToken(updatedToken);

        if (isDev) {
          console.log("✅ [AUTH-REFRESH] Token state updated");
        }
      } catch (error) {
        console.error("❌ [AUTH-REFRESH] Failed to refresh token:", error);

        // If refresh fails, user needs to re-authenticate
        setError("Your session could not be renewed. Please sign in again.");
        handleAuthExpiration();
      }
    };

    // Calculate when to refresh (5 minutes before expiration)
    const timeRemaining = getTokenTimeRemaining();
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes
    const refreshTime = Math.max(0, timeRemaining - refreshBuffer);

    if (isDev) {
      console.log("🔄 [AUTH-REFRESH] Automatic token refresh scheduled");
      console.log("   - Time until refresh:", Math.floor(refreshTime / 1000 / 60), "minutes");
      console.log("   - Time until expiration:", Math.floor(timeRemaining / 1000 / 60), "minutes");
    }

    // Schedule refresh
    const timeoutId = setTimeout(() => {
      refreshToken();
    }, refreshTime);

    // Also check every minute if we missed the refresh window
    const intervalId = setInterval(() => {
      const remaining = getTokenTimeRemaining();
      if (remaining <= refreshBuffer && remaining > 0) {
        if (isDev) {
          console.log("🔄 [AUTH-REFRESH] Refresh window reached via interval check");
        }
        refreshToken();
      } else if (remaining <= 0) {
        // Token expired - try to refresh if we have a refresh token
        if (token.refresh_token) {
          if (isDev) {
            console.log("🔄 [AUTH-REFRESH] Access token expired, refreshing with refresh token");
          }
          refreshToken();
        } else {
          // No refresh token - force re-login
          if (isDev) {
            console.log("❌ [AUTH-REFRESH] Token expired and no refresh token available");
          }
          handleAuthExpiration();
        }
      }
    }, 60000); // Check every minute

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [token]);

  // Effect to warm up schema cache when input screen loads
  useEffect(() => {
    // Only run warmup if:
    // 1. User is authenticated (has token and profile)
    // 2. Not showing confirmation screen (on input screen)
    // 3. Not currently parsing
    // 4. Warmup is needed (12+ hours since last request)
    if (token && userProfile && !showConfirmation && !isParsing && needsWarmup()) {
      const isDev =
        window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

      if (isDev) {
        console.log("🔥 [WARMUP] Starting schema cache warmup");
      }

      // Perform warmup request in background
      warmupSchemaCache(token.access_token, userProfile)
        .then(() => {
          // Record that a request was made
          recordRequest();
          if (isDev) {
            console.log("✅ [WARMUP] Schema cache warmed up and timestamp recorded");
          }
        })
        .catch(() => {
          // Silently fail - warmup is not critical
          if (isDev) {
            console.log("⚠ [WARMUP] Warmup failed, but this is non-critical");
          }
        });
    }
  }, [token, userProfile, showConfirmation, isParsing]);

  // Effect to load contacts when authenticated
  useEffect(() => {
    if (!token || contactsLoaded) return;

    const loadContacts = async () => {
      try {
        const response = await fetch("/api/contacts", {
          headers: {
            Authorization: `Bearer ${token.access_token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setContacts(data);
          setContactsLoaded(true);
        }
      } catch (err) {
        console.error("Error loading contacts:", err);
      }
    };

    loadContacts();
  }, [token, contactsLoaded]);

  // Handle import completion - updates contacts state and shows toast
  const handleImportComplete = (newContacts: Contact[], updatedContacts: Contact[]) => {
    // Add new contacts to state
    if (newContacts.length > 0) {
      setContacts((prev) => [...prev, ...newContacts]);
    }

    // Update existing contacts in state
    if (updatedContacts.length > 0) {
      setContacts((prev) =>
        prev.map((c) => {
          const updated = updatedContacts.find((u) => u.contact_id === c.contact_id);
          return updated || c;
        })
      );
    }

    // Show success toast
    const addedMsg = newContacts.length > 0 ? `${newContacts.length} added` : "";
    const updatedMsg = updatedContacts.length > 0 ? `${updatedContacts.length} updated` : "";
    const message = [addedMsg, updatedMsg].filter(Boolean).join(", ");
    setImportToast({ message: `Import complete: ${message}`, type: "success" });

    // Clear toast after 5 seconds
    setTimeout(() => setImportToast(null), 5000);

    // Switch to contacts tab to see the results
    setActiveTab("contacts");
  };

  // Handle opening email modal from contacts view
  const handleOpenEmailModal = (selectedContacts: Contact[]) => {
    setEmailModalContacts(selectedContacts);
  };

  // Handle sending bulk email
  const handleSendEmail = async (
    subject: string,
    htmlBody: string,
    recipients: Contact[],
    attachments: File[],
    draftId?: string,
    templateId?: string,
    templateName?: string
  ) => {
    if (!token || !userProfile?.email) {
      throw new Error("Not authenticated");
    }

    const isDev =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    setIsSendingEmail(true);
    try {
      const emailData = {
        subject,
        htmlBody,
        recipients: recipients.map((c) => ({
          contact_id: c.contact_id,
          email: c.email,
          name: c.full_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Unknown",
          first_name: c.first_name,
          last_name: c.last_name,
        })),
        sender: {
          email: userProfile.email,
          name: userProfile.name,
        },
        dryRun: isDev, // In dev mode, save to history but don't actually send
        draftId, // If sending from a draft, pass the draft ID so it can be deleted
        templateId, // Track which template was used (if any)
        templateName, // Human-readable template name
      };

      // In development, log details and use dryRun mode (saves to history but doesn't send)
      if (isDev) {
        console.log("═══════════════════════════════════════════════════════");
        console.log("  DEV MODE: Email will be saved to history (not sent)");
        console.log("═══════════════════════════════════════════════════════");
        console.log("From:", `${userProfile.name} <${userProfile.email}>`);
        console.log("Subject:", subject);
        console.log("Recipients:", recipients.length);
        recipients.forEach((r, i) => {
          console.log(`  ${i + 1}. ${r.full_name || r.email} <${r.email}>`);
        });
        console.log("HTML Body:", htmlBody);
        console.log("Attachments:", attachments.length);
        attachments.forEach((a, i) => {
          console.log(`  ${i + 1}. ${a.name} (${a.size} bytes)`);
        });
        console.log("═══════════════════════════════════════════════════════");
      }

      // Use FormData for multipart/form-data when attachments present
      const formData = new FormData();
      formData.append("emailData", JSON.stringify(emailData));
      attachments.forEach((file, index) => {
        formData.append(`attachment_${index}`, file);
      });

      const response = await fetch("/api/emails/send", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.access_token}`,
          // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to send email");
      }

      // Success - close modal and show toast
      setEmailModalContacts(null);
      setImportToast({
        message: isDev
          ? `[DEV] Email logged for ${recipients.length} recipient${recipients.length !== 1 ? "s" : ""}`
          : `Email sent to ${recipients.length} recipient${recipients.length !== 1 ? "s" : ""}`,
        type: "success",
      });
      setTimeout(() => setImportToast(null), 5000);

      // Trigger email history and drafts refresh (in case this was a draft being sent)
      handleEmailSentSuccess();

      // Background bounce check — fires after 5s, server sleeps 2min before processing
      // Only runs if the user has granted gmail.readonly (optional scope)
      const hasReadScope = token.scope?.includes("https://www.googleapis.com/auth/gmail.readonly");
      if (!isDev && hasReadScope) {
        setTimeout(() => {
          fetch("/api/emails/check-bounces", {
            method: "POST",
            headers: { Authorization: `Bearer ${token.access_token}` },
          }).catch(() => {});
        }, 5000);
      } else if (!isDev && !hasReadScope) {
        setImportToast({
          message: "Bounce detection unavailable — please sign out and sign in again to grant inbox read access.",
          type: "error",
        });
        setTimeout(() => setImportToast(null), 8000);
      }
    } catch (err) {
      console.error("Error sending email:", err);
      throw err;
    } finally {
      setIsSendingEmail(false);
    }
  };

  /**
   * Handle editing a draft
   */
  const handleEditDraft = async (draftId: string) => {
    try {
      const response = await fetch(`/api/emails/drafts/${draftId}`, {
        headers: {
          Authorization: `Bearer ${token?.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to load draft");
      }

      const data = await response.json();
      const draft = data.draft;

      // Set draft data and contacts to open the modal
      setDraftToEdit({
        draftId: draft.email_id,
        subject: draft.subject,
        htmlBody: draft.html_body,
        recipients: draft.recipients,
      });
      setEmailModalContacts(draft.recipients);
    } catch (err) {
      console.error("Error loading draft:", err);
      setImportToast({
        message: "Failed to load draft",
        type: "error",
      });
      setTimeout(() => setImportToast(null), 5000);
    }
  };

  /**
   * State for triggering drafts refresh
   */
  const [draftsSavedTrigger, setDraftsSavedTrigger] = useState(0);

  /**
   * Handle draft saved callback
   */
  const handleDraftSaved = () => {
    // Trigger drafts list refresh by incrementing counter
    setDraftsSavedTrigger((prev) => prev + 1);
  };

  /**
   * State for triggering email history refresh after sending
   */
  const [emailSentTrigger, setEmailSentTrigger] = useState(0);

  /**
   * Handle email sent callback
   */
  const handleEmailSentSuccess = () => {
    // Trigger both sent and drafts list refresh by incrementing counter
    setEmailSentTrigger((prev) => prev + 1);
  };

  /**
   * Handle authentication expiration
   * Clears all auth state and cache
   */
  const handleAuthExpiration = () => {
    googleLogout();
    clearAuthCache(); // Clear localStorage cache
    setToken(null);
    setUserProfile(null);
  };

  /**
   * Logout user
   * Clears all auth state and cache
   */
  const logout = () => {
    handleAuthExpiration();
    // Clear any error messages on logout
    setError(null);
    setAddSuccess(null);
  };

  /**
   * OAuth Authorization Code Flow with PKCE
   * Generates code verifier/challenge and redirects to Google OAuth
   * Request offline access to get refresh token for automatic token renewal
   */
  const login = async () => {
    // Clear any existing auth cache before login
    clearAuthCache();
    setToken(null);
    setUserProfile(null);

    const clientId =
      import.meta.env.VITE_GOOGLE_CLIENT_ID ||
      "781149858383-sjhps7mops95gjmqpbjugm4hk8uup5nv.apps.googleusercontent.com";

    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Save code verifier for later (will be used after redirect)
    savePKCEVerifier(codeVerifier);

    // Always show consent screen to avoid double sign-in flow
    const isDev =
      window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: window.location.origin,
      response_type: "code", // Authorization Code Flow (not implicit)
      scope:
        "https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/contacts.readonly https://www.googleapis.com/auth/drive.file openid",
      access_type: "offline", // Request refresh token for automatic renewal
      prompt: "consent", // Always show consent to avoid double sign-in flow
      code_challenge: codeChallenge,
      code_challenge_method: "S256", // SHA-256
    });

    if (isDev) {
      console.log("🔐 [AUTH-LOGIN] Starting OAuth Authorization Code Flow with PKCE");
      console.log("   - Client ID:", clientId);
      console.log("   - Redirect URI:", window.location.origin);
      console.log("   - Prompt: consent (always shown to avoid double sign-in)");
      console.log("   - Code verifier (first 10 chars):", codeVerifier.substring(0, 10) + "...");
      console.log("   - Code challenge (first 10 chars):", codeChallenge.substring(0, 10) + "...");
    }

    // Redirect to Google OAuth
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const handleParse = async () => {
    if (!token) {
      setError("Please sign in first.");
      return;
    }
    if (!userProfile?.email) {
      setError("Could not retrieve your user profile details. Please try signing in again.");
      return;
    }

    // Check if token is expired before making API call
    if (isTokenExpired()) {
      setError("Your session has expired. Please sign in again.");
      handleAuthExpiration();
      return;
    }

    setIsParsing(true);
    setError(null);
    setAddSuccess(null);
    setProgress(null);
    setDisplayProgress(0);

    try {
      // Call Claude AI to parse events — tabs are fully independent
      const operations = await parseEventsFromTextWithProgress(
        eventInputMode === "text" ? userInput : "",
        userProfile,
        token.access_token,
        (progressUpdate: ProgressUpdate) => {
          setProgress(progressUpdate);
        },
        ...(eventInputMode === "file" ? [fileContent, fileMediaType] as const : [])
      );

      // Show 100% completion briefly before clearing
      setDisplayProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 300)); // Show completion for 300ms

      // Clear progress when complete
      setProgress(null);
      setDisplayProgress(0);

      // Record successful request for warmup cache management
      recordRequest();

      if (operations && operations.length > 0) {
        setParsedOperations(operations);
        setShowConfirmation(true);
      } else {
        setError(
          eventInputMode === "file"
            ? "Couldn't extract any event details from this file. Please make sure it contains event information like a date, time, or event name, and try again."
            : "I couldn't find any actionable events in your description. Please try rephrasing or be more specific, for example tell me the name and/or time of a meeting you want to change."
        );
      }
    } catch (e) {
      console.error("Error in handleParse:", e);
      setProgress(null); // Clear progress on error
      setDisplayProgress(0);

      // Handle authentication errors
      if (e instanceof Error && e.message === "UNAUTHORIZED") {
        setError("Your session has expired. Please sign in again.");
        handleAuthExpiration();
      } else if (e instanceof Error) {
        // Check if it's a detailed error from the server
        const errorStage = (e as { stage?: string }).stage;

        // Show detailed error message
        if (errorStage === "calendar_fetch") {
          setError(
            `Failed to fetch your calendar events: ${e.message}\n\nPlease check that your Google Calendar permissions are correct and try again.`
          );
        } else if (errorStage === "ai_processing") {
          setError(
            `AI processing error: ${e.message}\n\nPlease try rephrasing your request or try again later.`
          );
        } else {
          // Show the full error message from the service
          setError(
            `Error: ${e.message}\n\nIf this persists, please check the console for more details.`
          );
        }
      } else {
        setError("Sorry, an unexpected error occurred. Please try again.");
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleOperationAttachmentsChange = (operationIndex: number, files: File[]) => {
    setOperationAttachments((prev) => {
      const next = new Map(prev);
      if (files.length === 0) {
        next.delete(operationIndex);
      } else {
        next.set(operationIndex, files);
      }
      return next;
    });
  };

  /** Safely parse JSON from a response, returning null if the body is not valid JSON. */
  const safeResponseJson = async (response: Response) => {
    try {
      return await response.json();
    } catch (e) {
      console.warn("Failed to parse error response as JSON:", e);
      return null;
    }
  };

  /**
   * Upload a file to Google Drive and set public read permissions.
   * Returns the Drive file metadata needed for Calendar attachments,
   * plus the file ID for cleanup on failure.
   */
  const uploadFileToDrive = async (
    file: File,
    accessToken: string
  ): Promise<CalendarAttachment & { fileId: string }> => {
    const metadata = {
      name: file.name,
      mimeType: file.type || "application/octet-stream",
    };

    const formData = new FormData();
    formData.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    formData.append("file", file);

    const response = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,webViewLink,iconLink",
      {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await safeResponseJson(response);
      throw new Error(
        errorData?.error?.message ||
          `Failed to upload ${file.name} to Google Drive (${response.status})`
      );
    }

    const driveFile = await response.json();

    if (!driveFile.id || !driveFile.webViewLink) {
      throw new Error(
        `Drive upload for ${file.name} returned an incomplete response (missing id or webViewLink)`
      );
    }

    // Make the file readable by anyone with the link so event attendees can access it
    const permResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files/${driveFile.id}/permissions`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ role: "reader", type: "anyone" }),
      }
    );

    if (!permResponse.ok) {
      // Clean up the uploaded file since we can't share it
      try {
        await fetch(`https://www.googleapis.com/drive/v3/files/${driveFile.id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      } catch {
        // Best-effort cleanup
      }
      const permError = await safeResponseJson(permResponse);
      throw new Error(
        permError?.error?.message ||
          `Failed to set sharing permissions for ${file.name} (${permResponse.status})`
      );
    }

    return {
      fileId: driveFile.id,
      fileUrl: driveFile.webViewLink,
      title: driveFile.name,
      mimeType: driveFile.mimeType,
      iconLink: driveFile.iconLink,
    };
  };

  /** Attempt to delete orphaned Drive files that were uploaded before a failure. */
  const cleanupDriveFiles = async (fileIds: string[], accessToken: string) => {
    for (const id of fileIds) {
      try {
        await fetch(`https://www.googleapis.com/drive/v3/files/${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      } catch {
        // Best-effort cleanup — don't block on failure
      }
    }
  };

  const handleConfirm = async () => {
    if (!token) {
      setError("You must be signed in to add events to your calendar.");
      return;
    }

    // Check if token is expired before making API calls
    if (isTokenExpired()) {
      setError("Your session has expired. Please sign in again.");
      handleAuthExpiration();
      return;
    }

    setIsAdding(true);
    setError(null);
    setAddSuccess(null);

    let successCount = 0;
    for (let opIndex = 0; opIndex < parsedOperations.length; opIndex++) {
      const operation = parsedOperations[opIndex];
      try {
        // Re-check token before each operation in case it expired during a long upload sequence
        if (isTokenExpired()) {
          throw new Error("Your session expired during processing. Please sign in and try again.");
        }

        let response: Response;
        const { type, eventId, event, responseStatus } = operation;

        switch (type) {
          case "CREATE": {
            const files = operationAttachments.get(opIndex);
            let eventBody = event;
            const uploadedFileIds: string[] = [];

            if (files && files.length > 0) {
              const driveAttachments: CalendarAttachment[] = [];

              try {
                for (let i = 0; i < files.length; i++) {
                  setAddSuccess(`Uploading file ${i + 1} of ${files.length}...`);
                  const result = await uploadFileToDrive(files[i], token.access_token);
                  uploadedFileIds.push(result.fileId);
                  const { fileId: _, ...attachment } = result;
                  driveAttachments.push(attachment);
                }
              } catch (uploadError) {
                // Clean up any files that were already uploaded to Drive
                await cleanupDriveFiles(uploadedFileIds, token.access_token);
                throw uploadError;
              }

              setAddSuccess("Creating calendar event...");
              eventBody = { ...event, attachments: driveAttachments };
            }

            const createUrl =
              "https://www.googleapis.com/calendar/v3/calendars/primary/events" +
              (files && files.length > 0 ? "?supportsAttachments=true" : "");

            response = await fetch(createUrl, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(eventBody),
            });

            // If Calendar API fails after uploading files, clean up orphaned Drive files
            if (!response.ok && uploadedFileIds.length > 0) {
              try {
                await cleanupDriveFiles(uploadedFileIds, token.access_token);
              } catch (cleanupErr) {
                console.warn(
                  "Failed to clean up Drive files after Calendar API error:",
                  cleanupErr
                );
              }
            }
            break;
          }

          case "UPDATE":
            if (!eventId) throw new Error("Event ID is missing for update operation.");
            response = await fetch(
              `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
              {
                method: "PUT", // PUT replaces the entire event
                headers: {
                  Authorization: `Bearer ${token.access_token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(event),
              }
            );
            break;

          case "DELETE":
            if (!eventId) throw new Error("Event ID is missing for delete operation.");
            response = await fetch(
              `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${token.access_token}`,
                },
              }
            );
            break;

          case "RSVP": {
            if (!eventId || !responseStatus)
              throw new Error("Event ID or response status is missing for RSVP operation.");
            if (!userProfile?.email) throw new Error("User email not available for RSVP.");

            const originalEvent = operation.originalEvent;
            if (!originalEvent?.attendees)
              throw new Error("Attendees list not available for RSVP.");

            const updatedAttendees = originalEvent.attendees.map(
              (attendee: {
                email: string;
                displayName?: string;
                responseStatus?: "needsAction" | "declined" | "tentative" | "accepted";
                self?: boolean;
              }) => {
                if (
                  attendee.self ||
                  attendee.email.toLowerCase() === userProfile.email.toLowerCase()
                ) {
                  return { ...attendee, responseStatus };
                }
                return attendee;
              }
            );

            response = await fetch(
              `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}?sendNotifications=true`,
              {
                method: "PATCH",
                headers: {
                  Authorization: `Bearer ${token.access_token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ attendees: updatedAttendees }),
              }
            );
            break;
          }

          default:
            // Should not happen
            throw new Error(`Unknown operation type: ${type}`);
        }

        if (!response.ok) {
          const errorData = await safeResponseJson(response);
          throw new Error(
            errorData?.error?.message ||
              `Failed to ${type.toLowerCase()} event (${response.status})`
          );
        }
        successCount++;
      } catch (e) {
        console.error(`Error performing ${operation.type} on event:`, e);
        setAddSuccess(null);
        const message = e instanceof Error ? e.message : "An unexpected error occurred";
        setError(
          `Failed to ${operation.type.toLowerCase()} "${operation.event.summary}": ${message}`
        );
        // Stop on first error
        break;
      }
    }

    setIsAdding(false);

    if (successCount > 0) {
      setAddSuccess(`Successfully applied ${successCount} change(s) to your calendar!`);
    }

    if (successCount === parsedOperations.length) {
      // All successful, clear everything
      setOperationAttachments(new Map());
      setShowConfirmation(false);
      setUserInput("");
      setFileContent(null);
      setFileMediaType(null);
      setParsedOperations([]);
      setTimeout(() => setAddSuccess(null), 5000); // Clear success message after 5s
    }
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setParsedOperations([]);
    setOperationAttachments(new Map());
    // Only clear input for the active tab — tabs are independent
    if (eventInputMode === "file") {
      // File uploads always need re-uploading
      setFileContent(null);
      setFileMediaType(null);
    }
    // Text input is kept so the user can edit and re-submit
  };

  // Require auth for all tabs
  if (!token) {
    const authRedirectReason = sessionStorage.getItem("authRedirectReason");
    if (authRedirectReason) sessionStorage.removeItem("authRedirectReason");
    return (
      <div className="bg-navy-darkest text-text-primary min-h-screen font-body">
        <GoogleAuth userProfile={userProfile} onLogout={logout} />
        <main className="container mx-auto p-4 md:p-8 max-w-3xl">
          <Header />
          <div className="mt-8 bg-navy-deep border border-border-strong rounded p-6 md:p-8">
            <div className="text-center">
              {authRedirectReason && (
                <div className="mb-6 px-4 py-3 rounded bg-red-900/40 border border-red-700 text-red-300 text-sm">
                  {authRedirectReason}
                </div>
              )}
              <p className="text-lg text-text-secondary mb-6">
                Please sign in with your Google account to get started.
              </p>
              <LoginButton onLogin={() => login()} />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-navy-darkest text-text-primary min-h-screen font-body">
      <GoogleAuth userProfile={userProfile} onLogout={logout} />

      {/* Tab Content */}
      {activeTab === "calendar" ? (
        <main className="container mx-auto p-4 md:p-6 max-w-5xl h-[calc(100vh-100px)] flex flex-col">
          <Header />
          <div className="mt-4">
            <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
          <div className="bg-navy-deep border border-border-strong rounded p-4 md:p-6 flex-1 min-h-0 flex flex-col overflow-hidden">
            {showConfirmation ? (
              <EventConfirmation
                operations={parsedOperations}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                isAdding={isAdding}
                operationAttachments={operationAttachments}
                onAttachmentsChange={handleOperationAttachmentsChange}
              />
            ) : (
              <EventInput
                userInput={userInput}
                setUserInput={setUserInput}
                fileContent={fileContent}
                setFileContent={setFileContent}
                fileMediaType={fileMediaType}
                setFileMediaType={setFileMediaType}
                inputMode={eventInputMode}
                setInputMode={setEventInputMode}
                onParse={handleParse}
                isLoading={isParsing}
                progress={progress}
                displayProgress={displayProgress}
                error={error}
                successMessage={addSuccess}
              />
            )}
          </div>
        </main>
      ) : activeTab === "contacts" ? (
        <main className="container mx-auto p-4 md:p-6 max-w-5xl h-[calc(100vh-100px)] flex flex-col">
          <Header />
          <div className="mt-4">
            <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
          <div className="bg-navy-deep border border-border-strong rounded p-4 md:p-6 flex-1 min-h-0 flex flex-col overflow-hidden">
            <ContactsView
              accessToken={token.access_token}
              onSendEmail={handleOpenEmailModal}
              clearSelectionTrigger={clearSelectionTrigger}
            />
          </div>
          {/* Import Toast - positioned higher to avoid floating action bar */}
          {importToast && (
            <div className="fixed bottom-20 right-4 z-50 animate-in slide-in-from-bottom-2 fade-in duration-200">
              <div
                className={`flex items-center gap-3 px-4 py-3 rounded shadow-lg border ${
                  importToast.type === "error"
                    ? "bg-red-900/90 border-red-800 text-red-100"
                    : "bg-emerald-900/90 border-emerald-800 text-emerald-100"
                }`}
              >
                <p className="text-sm font-medium">{importToast.message}</p>
                <button
                  onClick={() => setImportToast(null)}
                  className="p-1 rounded-sm hover:bg-emerald-800"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </main>
      ) : activeTab === "import" ? (
        <main className="container mx-auto p-4 md:p-6 max-w-5xl h-[calc(100vh-100px)] flex flex-col">
          <Header />
          <div className="mt-4">
            <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
          <div className="bg-navy-deep border border-border-strong rounded p-4 md:p-6 flex-1 min-h-0 flex flex-col overflow-hidden">
            <ImportContactsView
              accessToken={token.access_token}
              existingContacts={contacts}
              onImportComplete={handleImportComplete}
            />
          </div>
        </main>
      ) : (
        <main className="container mx-auto p-4 md:p-6 max-w-5xl h-[calc(100vh-100px)] flex flex-col">
          <Header />
          <div className="mt-4">
            <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
          </div>
          <div className="bg-navy-deep border border-border-strong rounded p-4 md:p-6 flex-1 min-h-0 flex flex-col overflow-hidden">
            <EmailHistoryView
              accessToken={token.access_token}
              onEditDraft={handleEditDraft}
              draftsSavedTrigger={draftsSavedTrigger}
              emailSentTrigger={emailSentTrigger}
            />
          </div>
        </main>
      )}

      {/* Bulk Email Modal */}
      {emailModalContacts && userProfile && (
        <BulkEmailModal
          contacts={emailModalContacts}
          onClose={() => {
            setEmailModalContacts(null);
            setDraftToEdit(null);
          }}
          onSend={handleSendEmail}
          isSending={isSendingEmail}
          senderEmail={userProfile.email}
          senderName={userProfile.name}
          senderPhoto={userProfile.picture}
          onSuccess={() => {
            setClearSelectionTrigger((prev) => prev + 1);
            setDraftToEdit(null);
          }}
          accessToken={token?.access_token || ""}
          draftData={
            draftToEdit
              ? {
                  draftId: draftToEdit.draftId,
                  subject: draftToEdit.subject,
                  htmlBody: draftToEdit.htmlBody,
                }
              : undefined
          }
          onDraftSaved={handleDraftSaved}
        />
      )}
    </div>
  );
};

export default App;
